import { store } from "../redux/store";
import {
  getAllCategories,
  getCampaignSettings,
  getCombos,
  getDiscountPromotions,
  getDrinks,
  getFeedbacks,
  getFoodProducts,
  getOrders,
  getSpinPackages,
  getStaffs,
  withSilentPermissionErrors,
} from "../helpers/xhr";
import { validateAuth } from "../components/auth/helper";
import { setCombos } from "../redux/slices/ComboState";
import { setPackages } from "../redux/slices/PackageState";
import { setPromotions } from "../redux/slices/PromotionState";
import { setSettings } from "../redux/slices/CampaignSettingsState";
import {
  can,
  canAny,
  hasFeature,
  hasStaffSession,
} from "./permissions";

type BootstrapTask = () => Promise<unknown> | unknown;

const runSilently = async (task: BootstrapTask) => {
  try {
    await withSilentPermissionErrors(async () => {
      await task();
    });
  } catch (error: any) {
    if (error?.response?.status !== 403) {
      console.error("Background bootstrap request failed:", error);
    }
  }
};

const throwRouteAccessError = (
  status: number,
  title: string,
  message: string
) => {
  throw new Response(JSON.stringify({ title, message }), {
    status,
    statusText: title,
    headers: { "Content-Type": "application/json" },
  });
};

export const requireCmsPermission = async (permissions: string[]) => {
  await validateAuth(store.dispatch);
  if (hasStaffSession() && !canAny(permissions)) {
    throwRouteAccessError(
      403,
      "Access denied",
      "Your staff account does not have permission to access this page."
    );
  }
};

export const requireCmsFeature = async (featureKey: string, permissions: string[] = []) => {
  await validateAuth(store.dispatch);
  if (!hasFeature(featureKey)) {
    throwRouteAccessError(
      402,
      "Feature not available",
      "This feature is not enabled for this restaurant's current plan."
    );
  }

  if (hasStaffSession() && !canAny(permissions)) {
    throwRouteAccessError(
      403,
      "Access denied",
      "Your staff account does not have permission to access this page."
    );
  }
};

export const ensureProductCatalog = async (options: { categories?: boolean } = {}) => {
  if (!hasFeature("menu_management") || !can("product_view")) return;

  const productState = store.getState().Product;
  const tasks: BootstrapTask[] = [];

  if (!productState.drinks.length) {
    tasks.push(() =>
      getDrinks("Loader", store.dispatch, productState.drinkCategories.length > 0)
    );
  }
  if (!productState.food.length) {
    tasks.push(() =>
      getFoodProducts("Loader", store.dispatch, productState.foodCategories.length > 0)
    );
  }
  if (
    options.categories &&
    (!productState.drinkCategories.length || !productState.foodCategories.length)
  ) {
    tasks.push(() => getAllCategories(store.dispatch));
  }

  await Promise.all(tasks.map(runSilently));
};

export const ensurePromotions = async () => {
  if (!hasFeature("promotions") || !can("promotion_view")) return;

  const { promotions } = store.getState().Promotion;
  if (!promotions.length) {
    await runSilently(
      () =>
        getDiscountPromotions().then((items) =>
          store.dispatch(setPromotions(items))
        )
    );
  }
};

export const ensureCombos = async () => {
  if (!hasFeature("menu_management") || !can("product_view")) return;

  const { combos } = store.getState().Combo;
  if (!combos.length) {
    await runSilently(() =>
      getCombos().then((items) => store.dispatch(setCombos(items)))
    );
  }
};

export const ensureCampaign = async () => {
  if (!hasFeature("promotions") || !can("promotion_view")) return;

  const { packages } = store.getState().Package;
  const { settings } = store.getState().Settings;
  const tasks: BootstrapTask[] = [];

  if (!packages.length) {
    tasks.push(() =>
      getSpinPackages().then((items) => store.dispatch(setPackages(items)))
    );
  }
  if (!settings) {
    tasks.push(() =>
      getCampaignSettings().then((data) => store.dispatch(setSettings(data)))
    );
  }

  await Promise.all(tasks.map(runSilently));
};

export const bootstrapDashboard = async () => {
  await validateAuth(store.dispatch);

  const { feedbacks } = store.getState().Feedback;
  const { staffs } = store.getState().Staff;
  const { orders } = store.getState().Order;
  const tasks: BootstrapTask[] = [];

  if (hasFeature("menu_management") && can("product_view")) {
    tasks.push(() => ensureProductCatalog());
    tasks.push(() => ensureCombos());
  }
  if (hasFeature("promotions") && can("promotion_view")) {
    tasks.push(() => ensureCampaign());
    tasks.push(() => ensurePromotions());
  }
  if (hasFeature("staff_management") && can("staff_view") && !staffs.length) {
    tasks.push(() => getStaffs(store.dispatch));
  }
  if (hasFeature("orders") && can("order_view") && !orders.length) {
    tasks.push(() => getOrders(store.dispatch));
  }
  if (!hasStaffSession() && !feedbacks.length) {
    tasks.push(() => getFeedbacks(store.dispatch, 1, 100, true));
  }

  await Promise.all(tasks.map(runSilently));
};
